import { AppShell, Burger, Button, Group } from "@mantine/core";
import { IconArrowRightOutline18 } from "nucleo-ui-outline-18";
import { useTranslation } from "react-i18next";

import { ICON_SIZE } from "@mountayaapp/theme";

import { ROOT_DOMAIN } from "../config/endpoints";
import { resolveTab } from "../content/registry";
import { resolveLanguage } from "../locale/resolveLanguage";
import { DocsSearch } from "../search/DocsSearch";
import { DocsSearchIcon, DocsSearchTrigger } from "../search/DocsSearchTrigger";
import { DocsBookDemo } from "./DocsBookDemo";
import { DocsBrand } from "./DocsBrand";
import { DocsHeaderTabs } from "./DocsHeaderTabs";
import { useDocsPathname } from "./DocsPathnameContext";
import { DocsThemeToggle } from "./DocsThemeToggle";
import classes from "./docsHeader.module.css";

/**
 * DocsHeaderProps carry the mobile navbar disclosure: whether the collapsible
 * sidebar is revealed and the toggle the burger fires. The shell owns the state,
 * so the same value drives both the burger and the navbar's collapsed config.
 */
interface DocsHeaderProps {
  navbarOpened: boolean;
  onNavbarToggle: () => void;
}

/**
 * DocsHeader is the persistent top bar. It carries two rows so the two kinds of
 * chrome stop competing for one line: an action row holding what a reader reaches
 * for (the brand, the command palette, the calls to action), above the section
 * strip holding where they can go. The rows share one surface with a single border
 * beneath them both.
 *
 * The action row is a three-cell grid so the palette trigger sits in the middle of
 * the bar regardless of how wide the brand or the action cluster grow; on narrow
 * viewports the middle cell drops out and the palette collapses to an icon beside
 * the other actions. The burger appears only on a sidebar-framed page, since that
 * is the only layout with a sidebar to reveal.
 *
 * The header is present on every page, above both the sidebar layout and the bare
 * full-width one, so the section strip is the one navigation control every page
 * carries at every width.
 */
export const DocsHeader = ({ navbarOpened, onNavbarToggle }: DocsHeaderProps) => {
  const { t } = useTranslation("docs");
  const pathname = useDocsPathname();
  const language = resolveLanguage(pathname);
  const activeTab = resolveTab(pathname);

  return (
    <AppShell.Header className={classes.header}>
      <div className={classes.actionRow}>
        <Group align="center" gap="sm" wrap="nowrap">
          {activeTab.layout === "sidebar" ? (
            <Burger
              aria-label={t("nav.toggleSidebar")}
              hiddenFrom="sm"
              onClick={onNavbarToggle}
              opened={navbarOpened}
              size="sm"
            />
          ) : null}
          <DocsBrand />
          <DocsThemeToggle />
        </Group>

        <div className={classes.searchCell}>
          <DocsSearchTrigger />
        </div>

        <Group className={classes.actions} gap="sm" wrap="nowrap">
          <DocsSearchIcon />
          <Group gap="sm" visibleFrom="sm" wrap="nowrap">
            <DocsBookDemo />
            <Button
              component="a"
              href={ROOT_DOMAIN}
              rel="noopener noreferrer"
              rightSection={<IconArrowRightOutline18 size={ICON_SIZE.xs} />}
              target="_blank"
              variant="filled"
            >
              {t("nav.getStarted")}
            </Button>
          </Group>
        </Group>
      </div>

      <DocsHeaderTabs />

      <DocsSearch language={language} />
    </AppShell.Header>
  );
};
